import { tgpu, d, std } from 'typegpu';
import type { TgpuRoot, TgpuGuardedComputePipeline, TgpuBindGroup, TgpuBindGroupLayout, TgpuBuffer } from 'typegpu';
import type { StorageFlag, UniformFlag } from 'typegpu';
import type { AnyWgslData, WgslArray } from 'typegpu/data';
import type { SimulationConfig, VizMode } from '../../config/schema';
import type { System } from '../System';
import { DataCell } from './DataCell';
import { NbParams, MAX_NEIGHBOR, isNeighborVizMode, neighborModeNumber, clampNeighborCount } from './neighbor';

function createStorage<T extends AnyWgslData>(root: TgpuRoot, schema: AnyWgslData, data: unknown): TgpuBuffer<WgslArray<T>> & StorageFlag {
  return root.createBuffer(schema, data).$usage('storage') as unknown as TgpuBuffer<WgslArray<T>> & StorageFlag;
}
function createUniform<T extends AnyWgslData>(root: TgpuRoot, schema: AnyWgslData, data: unknown): TgpuBuffer<T> & UniformFlag {
  return root.createBuffer(schema, data).$usage('uniform') as unknown as TgpuBuffer<T> & UniformFlag;
}

const BlendParams = d.struct({ weight: d.f32, _pad1: d.f32, _pad2: d.f32, _pad3: d.f32 });
const TTRUniforms = d.struct({ currentIter: d.f32, minImprovement: d.f32, _pad0: d.f32, _pad1: d.f32 });

export abstract class DualStateSystem<
  TStateA extends AnyWgslData,
  TStateB extends AnyWgslData,
  TParams extends AnyWgslData,
  TDivParams extends AnyWgslData,
  TData extends AnyWgslData,
> implements System<TData> {
  abstract readonly key: System['key'];
  abstract readonly stateSize: number;

  protected root!: TgpuRoot;
  protected cellCount = 0;
  protected StateArrayA!: WgslArray<TStateA>;
  protected StateArrayB!: WgslArray<TStateB>;
  protected DataArray!: WgslArray<TData>;

  protected initLayout!: TgpuBindGroupLayout;
  protected stepLayout!: TgpuBindGroupLayout;
  protected accumulateLayout!: TgpuBindGroupLayout;
  protected initPipeline!: TgpuGuardedComputePipeline;
  protected stepPipeline!: TgpuGuardedComputePipeline;
  protected accumulatePipeline!: TgpuGuardedComputePipeline;
  protected initBG!: TgpuBindGroup;
  protected stepFwd!: TgpuBindGroup;
  protected stepBwd!: TgpuBindGroup;
  protected accA!: TgpuBindGroup;
  protected accB!: TgpuBindGroup;
  protected paramsBuffer!: TgpuBuffer<TParams> & UniformFlag;
  protected dataBuf!: TgpuBuffer<WgslArray<TData>> & StorageFlag;
  protected _readIndex: 0 | 1 = 0;

  protected pertStateAa!: TgpuBuffer<WgslArray<TStateA>> & StorageFlag;
  protected pertStateAb!: TgpuBuffer<WgslArray<TStateA>> & StorageFlag;
  protected pertStateBa!: TgpuBuffer<WgslArray<TStateB>> & StorageFlag;
  protected pertStateBb!: TgpuBuffer<WgslArray<TStateB>> & StorageFlag;
  protected divDataBuf!: TgpuBuffer<WgslArray<TData>> & StorageFlag;
  protected divParamsBuffer!: TgpuBuffer<TDivParams> & UniformFlag;
  protected divInitLayout!: TgpuBindGroupLayout;
  protected divStepLayout!: TgpuBindGroupLayout;
  protected divInitPipeline!: TgpuGuardedComputePipeline;
  protected divStepPipeline!: TgpuGuardedComputePipeline;
  protected divInitBG!: TgpuBindGroup;
  protected divStepFwd!: TgpuBindGroup;
  protected divStepBwd!: TgpuBindGroup;
  protected divFrameCounter = 0;
  protected divTrialsCompleted = 0;
  protected lastConfig!: SimulationConfig;
  protected divAccumBuf!: TgpuBuffer<WgslArray<TData>> & StorageFlag;
  protected blendParamsBuffer!: TgpuBuffer<typeof BlendParams> & UniformFlag;
  protected blendPipeline!: TgpuGuardedComputePipeline;
  protected blendBG!: TgpuBindGroup;

  protected snapA!: TgpuBuffer<WgslArray<TStateA>> & StorageFlag;
  protected snapB!: TgpuBuffer<WgslArray<TStateB>> & StorageFlag;
  protected ttrUniformsBuf!: TgpuBuffer<typeof TTRUniforms> & UniformFlag;
  protected snapCopyPipelineA!: TgpuGuardedComputePipeline;
  protected snapCopyPipelineB!: TgpuGuardedComputePipeline;
  protected snapCopyFromAa!: TgpuBindGroup;
  protected snapCopyFromAb!: TgpuBindGroup;
  protected snapCopyFromBa!: TgpuBindGroup;
  protected snapCopyFromBb!: TgpuBindGroup;
  protected ttrPipeline!: TgpuGuardedComputePipeline;
  protected ttrFwd!: TgpuBindGroup;
  protected ttrBwd!: TgpuBindGroup;

  protected nbEnabled = false;
  protected nbPertAa!: TgpuBuffer<WgslArray<TStateA>> & StorageFlag;
  protected nbPertAb!: TgpuBuffer<WgslArray<TStateA>> & StorageFlag;
  protected nbPertBa!: TgpuBuffer<WgslArray<TStateB>> & StorageFlag;
  protected nbPertBb!: TgpuBuffer<WgslArray<TStateB>> & StorageFlag;
  protected nbStackArrayA!: WgslArray<TStateA>;
  protected nbStackArrayB!: WgslArray<TStateB>;
  protected nbDataBuf!: TgpuBuffer<WgslArray<TData>> & StorageFlag;
  protected nbParamsBuffer!: TgpuBuffer<typeof NbParams> & UniformFlag;
  protected nbInitPipeline!: TgpuGuardedComputePipeline;
  protected nbStepPipeline!: TgpuGuardedComputePipeline;
  protected nbInitBG!: TgpuBindGroup;
  protected nbStepFwd!: TgpuBindGroup;
  protected nbStepBwd!: TgpuBindGroup;

  build(root: TgpuRoot, config: SimulationConfig, cellCount: number): void {
    this.root = root;
    this.cellCount = cellCount;
    this.StateArrayA = this.createStateArrayA(cellCount);
    this.StateArrayB = this.createStateArrayB(cellCount);
    this.DataArray = this.createDataArray(cellCount);

    this.paramsBuffer = createUniform<TParams>(root, this.paramsStruct(), this.buildParamsData(config));
    const stateAa = createStorage<TStateA>(root, this.StateArrayA, this.zeroStateA());
    const stateAb = createStorage<TStateA>(root, this.StateArrayA, this.zeroStateA());
    const stateBa = createStorage<TStateB>(root, this.StateArrayB, this.zeroStateB());
    const stateBb = createStorage<TStateB>(root, this.StateArrayB, this.zeroStateB());
    this.dataBuf = createStorage<TData>(root, this.DataArray, this.zeroData());

    const buildResult = this.buildGpuPipelines(root, stateAa, stateAb, stateBa, stateBb);
    this.initLayout = buildResult.initLayout;
    this.stepLayout = buildResult.stepLayout;
    this.accumulateLayout = buildResult.accumulateLayout;
    this.initPipeline = buildResult.initPipeline;
    this.stepPipeline = buildResult.stepPipeline;
    this.accumulatePipeline = buildResult.accumulatePipeline;
    this.initBG = buildResult.initBG;
    this.stepFwd = buildResult.stepFwd;
    this.stepBwd = buildResult.stepBwd;
    this.accA = buildResult.accA;
    this.accB = buildResult.accB;

    this.pertStateAa = createStorage<TStateA>(root, this.StateArrayA, this.zeroStateA());
    this.pertStateAb = createStorage<TStateA>(root, this.StateArrayA, this.zeroStateA());
    this.pertStateBa = createStorage<TStateB>(root, this.StateArrayB, this.zeroStateB());
    this.pertStateBb = createStorage<TStateB>(root, this.StateArrayB, this.zeroStateB());
    this.divDataBuf = createStorage<TData>(root, this.DataArray, this.zeroDivData());
    this.divParamsBuffer = createUniform<TDivParams>(root, this.divParamsStruct(), this.buildDivParamsData(config, 0, 0));

    const divResult = this.buildDivGpuPipelines(root, stateAa, stateAb, stateBa, stateBb);
    this.divInitLayout = divResult.divInitLayout;
    this.divStepLayout = divResult.divStepLayout;
    this.divInitPipeline = divResult.divInitPipeline;
    this.divStepPipeline = divResult.divStepPipeline;
    this.divInitBG = divResult.divInitBG;
    this.divStepFwd = divResult.divStepFwd;
    this.divStepBwd = divResult.divStepBwd;
    this._readIndex = 0;

    this.divAccumBuf = createStorage<TData>(root, this.DataArray, this.zeroDivData());
    this.blendParamsBuffer = createUniform<typeof BlendParams>(root, BlendParams, { weight: 1, _pad1: 0, _pad2: 0, _pad3: 0 });
    const blendArr = d.arrayOf(DataCell, this.cellCount);
    const blendLayout = tgpu.bindGroupLayout({
      accum: { storage: blendArr, access: 'mutable' },
      source: { storage: blendArr },
      params: { uniform: BlendParams },
    });
    const bLayout = blendLayout;
    const blendCell = (ci: number) => {
      'use gpu';
      const w = bLayout.$.params.weight;
      const src = bLayout.$.source[ci];
      const acc = bLayout.$.accum[ci];
      bLayout.$.accum[ci] = DataCell({
        r: std.mix(acc.r as number, src.r as number, w),
        g: std.mix(acc.g as number, src.g as number, w),
        b: std.mix(acc.b as number, src.b as number, w),
        a: std.mix(acc.a as number, src.a as number, w),
      });
    };
    this.blendPipeline = root.createGuardedComputePipeline(blendCell);
    this.blendBG = root.createBindGroup(blendLayout, {
      accum: this.divAccumBuf as never,
      source: this.divDataBuf as never,
      params: this.blendParamsBuffer,
    });

    this.snapA = createStorage<TStateA>(root, this.StateArrayA, this.zeroStateA());
    this.snapB = createStorage<TStateB>(root, this.StateArrayB, this.zeroStateB());
    this.ttrUniformsBuf = createUniform<typeof TTRUniforms>(root, TTRUniforms, { currentIter: 0, minImprovement: 0, _pad0: 0, _pad1: 0 });

    const cpA = tgpu.bindGroupLayout({ src: { storage: this.StateArrayA }, dst: { storage: this.StateArrayA, access: 'mutable' } });
    const cpB = tgpu.bindGroupLayout({ src: { storage: this.StateArrayB }, dst: { storage: this.StateArrayB, access: 'mutable' } });
    const cpLA = cpA; const cpLB = cpB;
    const StructA = this.StateArrayA.elementType as unknown as ((v: (typeof cpLA)['$']['src'][number]) => (typeof cpLA)['$']['dst'][number]);
    const StructB = this.StateArrayB.elementType as unknown as ((v: (typeof cpLB)['$']['src'][number]) => (typeof cpLB)['$']['dst'][number]);
    const copyA = (ci: number) => { 'use gpu'; cpLA.$.dst[ci] = StructA(cpLA.$.src[ci]); };
    const copyB = (ci: number) => { 'use gpu'; cpLB.$.dst[ci] = StructB(cpLB.$.src[ci]); };
    this.snapCopyPipelineA = root.createGuardedComputePipeline(copyA);
    this.snapCopyPipelineB = root.createGuardedComputePipeline(copyB);
    this.snapCopyFromAa = root.createBindGroup(cpA, { src: stateAa as never, dst: this.snapA as never });
    this.snapCopyFromAb = root.createBindGroup(cpA, { src: stateAb as never, dst: this.snapA as never });
    this.snapCopyFromBa = root.createBindGroup(cpB, { src: stateBa as never, dst: this.snapB as never });
    this.snapCopyFromBb = root.createBindGroup(cpB, { src: stateBb as never, dst: this.snapB as never });

    const ttrResult = this.buildClosenessOfReturnPipeline(root, stateAa, stateAb, stateBa, stateBb, this.snapA, this.snapB, this.dataBuf);
    this.ttrPipeline = ttrResult.ttrPipeline;
    this.ttrFwd = ttrResult.ttrFwd;
    this.ttrBwd = ttrResult.ttrBwd;

    this.nbEnabled = isNeighborVizMode(config.vizMode);
    if (this.nbEnabled) {
      const K = clampNeighborCount(config.neighborCount);
      this.nbStackArrayA = d.arrayOf(this.StateArrayA.elementType as unknown as TStateA, MAX_NEIGHBOR * cellCount);
      this.nbStackArrayB = d.arrayOf(this.StateArrayB.elementType as unknown as TStateB, MAX_NEIGHBOR * cellCount);
      const baseZeroA = this.zeroStateA();
      const baseZeroB = this.zeroStateB();
      const stackZeroA: Record<string, number>[] = [];
      const stackZeroB: Record<string, number>[] = [];
      for (let k = 0; k < MAX_NEIGHBOR; k++) {
        for (let i = 0; i < cellCount; i++) { stackZeroA.push(baseZeroA[i]); stackZeroB.push(baseZeroB[i]); }
      }
      this.nbPertAa = createStorage<TStateA>(root, this.nbStackArrayA, stackZeroA);
      this.nbPertAb = createStorage<TStateA>(root, this.nbStackArrayA, stackZeroA);
      this.nbPertBa = createStorage<TStateB>(root, this.nbStackArrayB, stackZeroB);
      this.nbPertBb = createStorage<TStateB>(root, this.nbStackArrayB, stackZeroB);
      this.nbDataBuf = createStorage<TData>(root, this.DataArray, this.zeroData());
      this.nbParamsBuffer = createUniform<typeof NbParams>(root, NbParams, this.buildNbParamsData(config, 0, 0, K));
      const nbResult = this.buildNeighborPipelines(root, stateAa, stateAb, stateBa, stateBb);
      this.nbInitPipeline = nbResult.nbInitPipeline;
      this.nbStepPipeline = nbResult.nbStepPipeline;
      this.nbInitBG = nbResult.nbInitBG;
      this.nbStepFwd = nbResult.nbStepFwd;
      this.nbStepBwd = nbResult.nbStepBwd;
    }
  }

  updateParams(config: SimulationConfig): void {
    this.paramsBuffer.write(this.buildParamsData(config) as never);
  }

  getData(vizMode: VizMode): TgpuBuffer<WgslArray<TData>> {
    if (vizMode === 'divergence' || vizMode === 'divergenceDistance') {
      return this.divTrialsCompleted > 0 ? this.divAccumBuf : this.divDataBuf;
    }
    if (isNeighborVizMode(vizMode)) {
      return this.nbDataBuf;
    }
    return this.dataBuf;
  }

  getReadIndex() { return this._readIndex; }

  initSim(): void {
    this.initPipeline.with(this.initBG).dispatchThreads(this.cellCount);
    this._readIndex = 0;
  }

  stepSim(): void {
    if (this._readIndex === 0) {
      this.stepPipeline.with(this.stepFwd).dispatchThreads(this.cellCount);
    } else {
      this.stepPipeline.with(this.stepBwd).dispatchThreads(this.cellCount);
    }
    this._readIndex = this._readIndex === 0 ? 1 : 0;
  }

  accumulateSim(): void {
    if (this._readIndex === 0) {
      this.accumulatePipeline.with(this.accA).dispatchThreads(this.cellCount);
    } else {
      this.accumulatePipeline.with(this.accB).dispatchThreads(this.cellCount);
    }
  }

  initDivergence(seed: number, perturb: number): void {
    this.divParamsBuffer.write(this.buildDivParamsData(this.lastConfig, seed, perturb) as never);
    this.divFrameCounter = 0;
    this.divInitPipeline.with(this.divInitBG).dispatchThreads(this.cellCount);
    this._readIndex = 0;
  }

  divergenceStep(): void {
    this.divFrameCounter++;
    const data = this.buildDivParamsData(this.lastConfig, 0, 0);
    data.frameCounter = this.divFrameCounter;
    this.divParamsBuffer.write(data as never);
    if (this._readIndex === 0) {
      this.divStepPipeline.with(this.divStepFwd).dispatchThreads(this.cellCount);
    } else {
      this.divStepPipeline.with(this.divStepBwd).dispatchThreads(this.cellCount);
    }
    this._readIndex = this._readIndex === 0 ? 1 : 0;
  }

  blendDivergenceTrial(completedTrials: number): void {
    const weight = 1.0 / (completedTrials + 1);
    this.blendParamsBuffer.write({ weight, _pad1: 0, _pad2: 0, _pad3: 0 } as never);
    this.blendPipeline.with(this.blendBG).dispatchThreads(this.cellCount);
    this.divTrialsCompleted = completedTrials + 1;
  }

  neighborInit(seed: number, perturb: number): void {
    if (!this.nbEnabled) return;
    const K = clampNeighborCount(this.lastConfig.neighborCount);
    this.nbParamsBuffer.write(this.buildNbParamsData(this.lastConfig, seed, perturb, K) as never);
    this.nbInitPipeline.with(this.nbInitBG).dispatchThreads(this.cellCount);
    this._readIndex = 0;
  }

  neighborStep(): void {
    if (!this.nbEnabled) return;
    if (this._readIndex === 0) {
      this.nbStepPipeline.with(this.nbStepFwd).dispatchThreads(this.cellCount);
    } else {
      this.nbStepPipeline.with(this.nbStepBwd).dispatchThreads(this.cellCount);
    }
    this._readIndex = this._readIndex === 0 ? 1 : 0;
  }

  takeSnapshot(): void {
    const cpA = this._readIndex === 0 ? this.snapCopyFromAa : this.snapCopyFromAb;
    const cpB = this._readIndex === 0 ? this.snapCopyFromBa : this.snapCopyFromBb;
    this.snapCopyPipelineA.with(cpA).dispatchThreads(this.cellCount);
    this.snapCopyPipelineB.with(cpB).dispatchThreads(this.cellCount);
  }

  closenessOfReturnStep(currentIter: number, minImprovement: number): void {
    this.ttrUniformsBuf.write({ currentIter, minImprovement, _pad0: 0, _pad1: 0 } as never);
    if (this._readIndex === 0) this.ttrPipeline.with(this.ttrFwd).dispatchThreads(this.cellCount);
    else this.ttrPipeline.with(this.ttrBwd).dispatchThreads(this.cellCount);
    this._readIndex = this._readIndex === 0 ? 1 : 0;
  }

  protected zeroData(): Record<string, number>[] {
    return Array.from({ length: this.cellCount }, () => ({ r: 0, g: 0, b: 0, a: 0 }));
  }

  protected zeroDivData(): Record<string, number>[] {
    return Array.from({ length: this.cellCount }, () => ({ r: 0, g: 0, b: 0, a: 1 }));
  }

  abstract createStateArrayA(cellCount: number): WgslArray<TStateA>;
  abstract createStateArrayB(cellCount: number): WgslArray<TStateB>;
  abstract createDataArray(cellCount: number): WgslArray<TData>;
  abstract paramsStruct(): TParams;
  abstract divParamsStruct(): TDivParams;

  abstract zeroStateA(): Record<string, number>[];
  abstract zeroStateB(): Record<string, number>[];

  abstract buildParamsData(config: SimulationConfig): Record<string, number>;
  abstract buildDivParamsData(config: SimulationConfig, seed: number, perturb: number): Record<string, number> & { frameCounter: number };

  buildNbParamsData(config: SimulationConfig, seed: number, perturb: number, K: number): Record<string, number> {
    return {
      nbMode: neighborModeNumber(config.vizMode),
      perturb,
      seed,
      perturbMode: config.perturbDistribution === 'gaussian' ? 1 : 0,
      K,
      frameCounter: 0,
      _pad0: 0,
      _pad1: 0,
    };
  }

  protected abstract buildGpuPipelines(
    root: TgpuRoot,
    stateAa: TgpuBuffer<WgslArray<TStateA>> & StorageFlag,
    stateAb: TgpuBuffer<WgslArray<TStateA>> & StorageFlag,
    stateBa: TgpuBuffer<WgslArray<TStateB>> & StorageFlag,
    stateBb: TgpuBuffer<WgslArray<TStateB>> & StorageFlag,
  ): GpuPipelinesResult;

  protected abstract buildDivGpuPipelines(
    root: TgpuRoot,
    stateAa: TgpuBuffer<WgslArray<TStateA>> & StorageFlag,
    stateAb: TgpuBuffer<WgslArray<TStateA>> & StorageFlag,
    stateBa: TgpuBuffer<WgslArray<TStateB>> & StorageFlag,
    stateBb: TgpuBuffer<WgslArray<TStateB>> & StorageFlag,
  ): DivGpuPipelinesResult;

  protected abstract buildClosenessOfReturnPipeline(
    root: TgpuRoot,
    stateAa: TgpuBuffer<WgslArray<TStateA>> & StorageFlag,
    stateAb: TgpuBuffer<WgslArray<TStateA>> & StorageFlag,
    stateBa: TgpuBuffer<WgslArray<TStateB>> & StorageFlag,
    stateBb: TgpuBuffer<WgslArray<TStateB>> & StorageFlag,
    snapA: TgpuBuffer<WgslArray<TStateA>> & StorageFlag,
    snapB: TgpuBuffer<WgslArray<TStateB>> & StorageFlag,
    dataBuf: TgpuBuffer<WgslArray<TData>> & StorageFlag,
  ): CORPipelineResult;

  protected abstract buildNeighborPipelines(
    root: TgpuRoot,
    stateAa: TgpuBuffer<WgslArray<TStateA>> & StorageFlag,
    stateAb: TgpuBuffer<WgslArray<TStateA>> & StorageFlag,
    stateBa: TgpuBuffer<WgslArray<TStateB>> & StorageFlag,
    stateBb: TgpuBuffer<WgslArray<TStateB>> & StorageFlag,
  ): NeighborPipelinesResult;
}

export interface GpuPipelinesResult {
  initLayout: TgpuBindGroupLayout;
  stepLayout: TgpuBindGroupLayout;
  accumulateLayout: TgpuBindGroupLayout;
  initPipeline: TgpuGuardedComputePipeline;
  stepPipeline: TgpuGuardedComputePipeline;
  accumulatePipeline: TgpuGuardedComputePipeline;
  initBG: TgpuBindGroup;
  stepFwd: TgpuBindGroup;
  stepBwd: TgpuBindGroup;
  accA: TgpuBindGroup;
  accB: TgpuBindGroup;
}

export interface DivGpuPipelinesResult {
  divInitLayout: TgpuBindGroupLayout;
  divStepLayout: TgpuBindGroupLayout;
  divInitPipeline: TgpuGuardedComputePipeline;
  divStepPipeline: TgpuGuardedComputePipeline;
  divInitBG: TgpuBindGroup;
  divStepFwd: TgpuBindGroup;
  divStepBwd: TgpuBindGroup;
}

export interface CORPipelineResult {
  ttrPipeline: TgpuGuardedComputePipeline;
  ttrFwd: TgpuBindGroup;
  ttrBwd: TgpuBindGroup;
}

export interface NeighborPipelinesResult {
  nbInitPipeline: TgpuGuardedComputePipeline;
  nbStepPipeline: TgpuGuardedComputePipeline;
  nbInitBG: TgpuBindGroup;
  nbStepFwd: TgpuBindGroup;
  nbStepBwd: TgpuBindGroup;
}
